import type { Envelope, ValidationDetail, ValidationErrors } from "./types";

/** One leaf of a crossbar 400: where it is, which rule failed, and why. */
export interface ValidationFault {
  /** The document path crossbar named: "numbers", "flow.children._.data.id". */
  field: string;
  /** The schema keyword: "required", "unique", "type" … */
  code: string;
  message: string;
  /** The offending value, when crossbar names it. */
  cause?: string;
}

/** Everything the client throws is one of these. */
export class ApiError extends Error {
  readonly status: number;
  readonly requestId?: string;
  readonly body?: Envelope<unknown>;
  constructor(message: string, status: number, body?: Envelope<unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.requestId = body?.request_id;
  }
}

export class ValidationError extends ApiError {
  readonly errors: ValidationErrors;
  constructor(body: Envelope<ValidationErrors>) {
    super(body.message ?? "validation error", 400, body);
    this.name = "ValidationError";
    this.errors = body.data ?? {};
  }
  /**
   * Every leaf of the body, with the schema keyword and the `cause` that
   * `fieldErrors()` drops. Crossbar nests the failed rule under the field
   * (`{numbers: {unique: {message, cause}}}`), so the last segment is the
   * code and the rest is the field — except at depth 1, where the field
   * would otherwise be empty and the key itself names it.
   */
  faults(): ValidationFault[] {
    return this.#walk().map(({ field, code, message, cause }) => ({ field, code, message, cause }));
  }

  /**
   * The same walk, keeping how many keys stood above the detail. Depth 0
   * is `{numbers: {message}}`: crossbar named no rule, so the key had to
   * serve as the field and `invalid` was made up for the code. Anything
   * deeper carries a real path, whatever the rule is called.
   */
  #walk(): (ValidationFault & { depth: number })[] {
    const out: (ValidationFault & { depth: number })[] = [];
    const walk = (node: unknown, path: string[]) => {
      if (!node || typeof node !== "object") return;
      for (const [rawKey, value] of Object.entries(node as Record<string, unknown>)) {
        // crossbar joins a nested path into one key with the dots escaped
        // (`flow%2Echildren%2E_%2Edata%2Eendpoints`); read it back as a path.
        const key = rawKey.replace(/%2E/gi, ".");
        if (value && typeof value === "object" && "message" in (value as object)) {
          const detail = value as ValidationDetail;
          out.push({
            field: path.join(".") || key,
            code: path.length ? key : "invalid",
            message: String(detail.message ?? key),
            cause: typeof detail.cause === "string" ? detail.cause : undefined,
            depth: path.length,
          });
        } else {
          walk(value, [...path, key]);
        }
      }
    };
    walk(this.errors, []);
    return out;
  }

  /** Flatten to `[{ path: "caller_id.internal.name", message }]` for form libraries. */
  fieldErrors(): { path: string; message: string }[] {
    // The form libraries want the path to the field, never the failed
    // rule, and a depth-0 fault has no path of its own to give them. A
    // fault whose rule is itself called `invalid`
    // (`{acme_eab_hmac: {invalid: {message}}}`) does have one.
    return this.#walk().map((f) => ({ path: f.depth === 0 ? "" : f.field, message: f.message }));
  }
}

/** 402: the server quoted a charge and wants `accept_charges: true` to proceed. */
export class PaymentRequiredError extends ApiError {
  readonly quote: unknown;
  constructor(body: Envelope<unknown>) {
    super("payment required", 402, body);
    this.name = "PaymentRequiredError";
    this.quote = body.data;
  }
}

export type UnauthorizedKind = "mfa_required" | "invalid_credentials" | "account_locked" | "expired";

export class UnauthorizedError extends ApiError {
  readonly kind: UnauthorizedKind;
  readonly multiFactorRequest?: { key_type?: string; provider_name?: string };
  constructor(body: Envelope<unknown> | undefined, kind: UnauthorizedKind) {
    super(kind, 401, body);
    this.name = "UnauthorizedError";
    this.kind = kind;
    const data = (body?.data ?? {}) as { multi_factor_request?: UnauthorizedError["multiFactorRequest"] };
    this.multiFactorRequest = data.multi_factor_request;
  }
}

export class ForbiddenError extends ApiError {
  readonly cause?: string;
  constructor(body?: Envelope<unknown>) {
    super("forbidden", 403, body);
    this.name = "ForbiddenError";
    this.cause = (body?.data as { cause?: string } | undefined)?.cause;
  }
}

export class RateLimitedError extends ApiError {
  constructor(body?: Envelope<unknown>) {
    super("too many requests", 429, body);
    this.name = "RateLimitedError";
  }
}

export function classify401(body?: Envelope<unknown>): UnauthorizedKind {
  const data = (body?.data ?? {}) as { multi_factor_request?: unknown; message?: string };
  if (data.multi_factor_request) return "mfa_required";
  if (body?.error === "account_is_locked" || /locked/i.test(String(data.message ?? ""))) return "account_locked";
  if (body?.message === "invalid_credentials" || /invalid credentials/i.test(String(data.message ?? ""))) return "invalid_credentials";
  return "expired";
}
